<template>
  <el-dialog
    :model-value="graphicCaptchaState.visible"
    width="360px"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="true"
    append-to-body
    align-center
    @close="handleClose"
  >
    <div class="w-full bg-white text-[#222226]">
      <div class="flex items-start justify-between px-4 pt-3">
        <div>
          <div class="text-[12px] leading-[17px] text-[#9d9da8]">
            {{ graphicCaptchaState.title }}
          </div>
          <div class="mt-1 text-[16px] font-medium leading-[22px] text-[#222226]">
            {{ graphicCaptchaState.description }}
          </div>
        </div>
        <button
          type="button"
          class="flex h-5 w-5 items-center justify-center text-[#9d9da8]"
          @click="handleClose"
        >
          <el-icon><Close /></el-icon>
        </button>
      </div>

      <div class="px-4 pt-3">
        <div
          class="relative mx-auto overflow-hidden rounded-[8px] bg-[#eef0f5]"
          :style="canvasStyle"
        >
          <img
            :key="graphicCaptchaState.challengeSeed"
            :src="graphicCaptchaState.bgImage"
            alt="图形验证"
            class="h-full w-full object-cover select-none"
            draggable="false"
          />
          <img
            v-if="graphicCaptchaState.sliderImage"
            :key="`${graphicCaptchaState.challengeSeed}-slider`"
            :src="graphicCaptchaState.sliderImage"
            alt="拼图块"
            class="absolute select-none pointer-events-none"
            draggable="false"
            :style="sliderPieceStyle"
          />

          <div
            v-if="graphicCaptchaState.loading || graphicCaptchaState.verifying"
            class="absolute inset-0 flex items-center justify-center bg-black/35 text-sm text-white"
          >
            {{ graphicCaptchaState.verifying ? "正在校验..." : "正在加载验证码..." }}
          </div>

          <div
            v-else-if="!graphicCaptchaState.bgImage"
            class="absolute inset-0 flex items-center justify-center bg-[#eef0f5] text-sm text-[#8c8fa3]"
          >
            验证码加载失败，请刷新重试
          </div>
        </div>
      </div>

      <div class="px-4 pb-3 pt-3">
        <div
          class="relative mx-auto h-10 rounded-full bg-[#efefef] px-2"
          :style="trackStyle"
        >
          <div
            class="absolute inset-y-1 left-2 rounded-full bg-[#5e628f]/15"
            :style="{ width: `${progressWidth}px` }"
          ></div>
          <div class="absolute inset-0 flex items-center justify-center text-[13px] text-[#b2b2b2]">
            {{ helperText }}
          </div>
          <div
            class="absolute left-2 top-1 flex h-8 w-12 items-center justify-center rounded-full bg-[#5e628f] text-white shadow-[0_4px_10px_rgba(94,98,143,0.35)]"
            :class="handleClassName"
            :style="{ transform: `translateX(${sliderOffset}px)` }"
            @pointerdown.prevent="startDrag"
          >
            <div class="flex items-center gap-1">
              <span class="block h-3 w-[2px] rounded bg-white/85"></span>
              <span class="block h-3 w-[2px] rounded bg-white/85"></span>
              <span class="block h-3 w-[2px] rounded bg-white/85"></span>
            </div>
          </div>
        </div>

        <div class="mt-3 flex items-center justify-between text-[12px] text-[#b6b6b6]">
          <div>
            {{ footerText }}
          </div>
          <div class="flex items-center gap-3">
            <button
              type="button"
              class="flex h-6 w-6 items-center justify-center text-[20px]"
              @click="handleRefresh"
            >
              <el-icon><RefreshRight /></el-icon>
            </button>
            <button
              type="button"
              class="flex h-6 w-6 items-center justify-center text-[20px]"
              @click="handleClose"
            >
              <el-icon><CircleClose /></el-icon>
            </button>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { computed, onBeforeUnmount, ref } from "vue";
import { CircleClose, Close, RefreshRight } from "@element-plus/icons-vue";
import {
  closeGraphicCaptcha,
  graphicCaptchaState,
  refreshGraphicCaptcha,
  verifyGraphicCaptcha,
} from "@/utils/graphicCaptchaService";

const sliderOffset = ref(0);
const dragging = ref(false);
const startClientX = ref(0);
const startOffset = ref(0);
const trace = ref([]);
const dragStartedAt = ref(0);
const HANDLE_WIDTH = 48;
const TRACK_PADDING = 8;

const imageWidth = computed(() => graphicCaptchaState.bgWidth || 320);
const imageHeight = computed(() => graphicCaptchaState.bgHeight || 160);
const pieceWidth = computed(() => graphicCaptchaState.jigsawWidth || 52);
const pieceHeight = computed(() => graphicCaptchaState.jigsawHeight || 52);
const maxHandleOffset = computed(() =>
  Math.max(imageWidth.value - HANDLE_WIDTH - TRACK_PADDING * 2, 0),
);
const maxPieceOffset = computed(() =>
  Math.max(imageWidth.value - pieceWidth.value, 0),
);
const canOperate = computed(
  () =>
    !!graphicCaptchaState.visible &&
    !!graphicCaptchaState.captchaId &&
    !graphicCaptchaState.loading &&
    !graphicCaptchaState.verifying,
);
const progressWidth = computed(() => sliderOffset.value + HANDLE_WIDTH);
const helperText = computed(() => {
  if (graphicCaptchaState.verifying) {
    return "正在校验，请稍候";
  }
  if (graphicCaptchaState.loading) {
    return "正在加载验证码";
  }
  return "拖动滑块完成拼图";
});
const footerText = computed(() => {
  if (graphicCaptchaState.loading) {
    return "加载中...";
  }
  if (typeof graphicCaptchaState.remainTimes === "number") {
    return `剩余重试次数 ${graphicCaptchaState.remainTimes}`;
  }
  if (graphicCaptchaState.expireSeconds > 0) {
    return `${graphicCaptchaState.expireSeconds}s 内完成验证`;
  }
  return "";
});
const handleClassName = computed(() =>
  canOperate.value ? "cursor-grab touch-none" : "cursor-not-allowed opacity-70",
);
const canvasStyle = computed(() => ({
  width: `${imageWidth.value}px`,
  height: `${imageHeight.value}px`,
}));
const trackStyle = computed(() => ({
  width: `${imageWidth.value}px`,
}));
const puzzleOffsetX = computed(() => mapHandleOffsetToPuzzleOffset(sliderOffset.value));
const sliderPieceStyle = computed(() => ({
  width: `${pieceWidth.value}px`,
  height: `${pieceHeight.value}px`,
  left: `${puzzleOffsetX.value}px`,
  top: `${graphicCaptchaState.offsetY || 0}px`,
}));

function resetSlider() {
  sliderOffset.value = 0;
  dragging.value = false;
  startClientX.value = 0;
  startOffset.value = 0;
  trace.value = [];
  dragStartedAt.value = 0;
}

function mapHandleOffsetToPuzzleOffset(handleOffset) {
  if (maxHandleOffset.value <= 0 || maxPieceOffset.value <= 0) {
    return 0;
  }

  return Math.round((handleOffset / maxHandleOffset.value) * maxPieceOffset.value);
}

function getClientX(event) {
  return event?.clientX ?? 0;
}

function createTracePoint(x, t) {
  return {
    x: Math.round(x),
    y: Math.round(graphicCaptchaState.offsetY || 0),
    t: Math.max(Math.round(t), 0),
  };
}

function recordTrace(x, t) {
  const point = createTracePoint(x, t);
  const latestPoint = trace.value[trace.value.length - 1];

  if (
    latestPoint &&
    latestPoint.x === point.x &&
    latestPoint.y === point.y &&
    latestPoint.t === point.t
  ) {
    return;
  }

  trace.value.push(point);
}

function buildTrace(finalX) {
  const elapsed = dragStartedAt.value
    ? performance.now() - dragStartedAt.value
    : 0;

  if (!trace.value.length) {
    trace.value.push(createTracePoint(0, 0));
  }

  const lastPoint = trace.value[trace.value.length - 1];
  if (!lastPoint || lastPoint.x !== Math.round(finalX)) {
    trace.value.push(createTracePoint(finalX, elapsed));
  }

  if (trace.value.length < 3) {
    const firstPoint = trace.value[0];
    const finalPoint = trace.value[trace.value.length - 1];
    const middlePoint = createTracePoint(
      Math.round((firstPoint.x + finalPoint.x) / 2),
      Math.max(finalPoint.t / 2, 1),
    );

    trace.value.splice(1, 0, middlePoint);
  }

  while (trace.value.length < 3) {
    const finalPoint = trace.value[trace.value.length - 1];
    trace.value.push(
      createTracePoint(finalPoint.x, Math.max(finalPoint.t, trace.value.length)),
    );
  }

  return trace.value.slice();
}

async function handleRefresh() {
  resetSlider();
  await refreshGraphicCaptcha();
}

function onMouseMove(event) {
  if (!dragging.value) {
    return;
  }

  const nextOffset = startOffset.value + getClientX(event) - startClientX.value;
  sliderOffset.value = Math.min(Math.max(nextOffset, 0), maxHandleOffset.value);

  recordTrace(
    mapHandleOffsetToPuzzleOffset(sliderOffset.value),
    performance.now() - dragStartedAt.value,
  );
}

async function onMouseUp() {
  if (!dragging.value) {
    return;
  }

  dragging.value = false;
  const finalX = mapHandleOffsetToPuzzleOffset(sliderOffset.value);
  const verificationTrace = buildTrace(finalX);

  try {
    const result = await verifyGraphicCaptcha({
      x: finalX,
      y: Math.round(graphicCaptchaState.offsetY || 0),
      trace: verificationTrace,
    });

    if (result?.status !== "passed") {
      resetSlider();
    }
  } catch (error) {
    resetSlider();
  }
}

function startDrag(event) {
  if (!canOperate.value) {
    return;
  }

  dragging.value = true;
  startClientX.value = getClientX(event);
  startOffset.value = sliderOffset.value;
  dragStartedAt.value = performance.now();
  trace.value = [createTracePoint(mapHandleOffsetToPuzzleOffset(sliderOffset.value), 0)];
}

function handleClose() {
  resetSlider();
  closeGraphicCaptcha();
}

window.addEventListener("pointermove", onMouseMove);
window.addEventListener("pointerup", onMouseUp);

onBeforeUnmount(() => {
  window.removeEventListener("pointermove", onMouseMove);
  window.removeEventListener("pointerup", onMouseUp);
});
</script>

<style scoped>
:deep(.el-dialog) {
  border-radius: 4px;
  padding: 0;
  overflow: hidden;
}

:deep(.el-dialog__header) {
  display: none;
}

:deep(.el-dialog__body) {
  padding: 0;
}
</style>
